<!--
  API Testing Center
  Centro de pruebas para APIs de formularios optimizados
-->

<template>
  <div class="api-testing-center">
    <v-container fluid class="pa-6">
      <!-- Header -->
      <v-row class="mb-4">
        <v-col cols="12">
          <v-card class="elevation-8">
            <v-card-title class="bg-gradient-primary text-white pa-4">
              <v-icon class="mr-3" size="32">mdi-api</v-icon>
              <div>
                <h2 class="text-h4 mb-1">API Testing Center</h2>
                <p class="text-body-1 mb-0 opacity-90">
                  Centro de pruebas para APIs de formularios optimizados
                </p>
              </div>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Panel de Control -->
      <v-row class="mb-4">
        <v-col cols="12" md="4">
          <v-card class="elevation-6 h-100">
            <v-card-title class="bg-primary text-white">
              <v-icon class="mr-2">mdi-test-tube</v-icon>
              Validación Async
            </v-card-title>
            <v-card-text class="pa-4">
              <div class="mb-3">
                <v-text-field
                  v-model="testEmail"
                  label="Test Email Validation"
                  type="email"
                  variant="outlined"
                  density="comfortable"
                  :loading="emailTesting"
                  @blur="testEmailValidation"
                ></v-text-field>
              </div>
              
              <div class="mb-3">
                <v-text-field
                  v-model="testUsername"
                  label="Test Username Validation"
                  variant="outlined"
                  density="comfortable"
                  :loading="usernameTesting"
                  @blur="testUsernameValidation"
                ></v-text-field>
              </div>

              <v-alert
                v-if="testResults.validation"
                :type="testResults.validation.success ? 'success' : 'error'"
                density="compact"
                class="mb-3"
              >
                {{ testResults.validation.message }}
              </v-alert>

              <v-btn
                color="primary"
                variant="elevated"
                prepend-icon="mdi-play"
                @click="runValidationTests"
                :loading="runningTests"
                block
              >
                Ejecutar Tests de Validación
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="elevation-6 h-100">
            <v-card-title class="bg-secondary text-white">
              <v-icon class="mr-2">mdi-upload</v-icon>
              File Upload Testing
            </v-card-title>
            <v-card-text class="pa-4">
              <v-file-input
                v-model="testFile"
                label="Test File Upload"
                variant="outlined"
                density="comfortable"
                accept="image/*,.pdf,.doc,.docx"
                prepend-icon="mdi-paperclip"
                @change="testFileUpload"
              ></v-file-input>

              <div v-if="uploadProgress > 0" class="mb-3">
                <v-progress-linear
                  :model-value="uploadProgress"
                  color="success"
                  height="8"
                  striped
                ></v-progress-linear>
                <p class="text-caption text-center mt-1">
                  {{ uploadProgress }}% completado
                </p>
              </div>

              <v-alert
                v-if="testResults.upload"
                :type="testResults.upload.success ? 'success' : 'error'"
                density="compact"
                class="mb-3"
              >
                {{ testResults.upload.message }}
              </v-alert>

              <v-btn
                color="secondary"
                variant="elevated"
                prepend-icon="mdi-cloud-upload"
                @click="simulateFileUpload"
                :loading="uploadTesting"
                block
              >
                Simular Upload
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="elevation-6 h-100">
            <v-card-title class="bg-success text-white">
              <v-icon class="mr-2">mdi-auto-fix</v-icon>
              Autocompletion Testing
            </v-card-title>
            <v-card-text class="pa-4">
              <v-autocomplete
                v-model="selectedDepartment"
                :items="departmentSuggestions"
                label="Test Department Autocomplete"
                variant="outlined"
                density="comfortable"
                :loading="autocompleteLoading"
                @update:search="searchDepartments"
              ></v-autocomplete>

              <v-autocomplete
                v-model="selectedUser"
                :items="userSuggestions"
                label="Test User Autocomplete"
                variant="outlined"
                density="comfortable"
                item-title="fullName"
                item-value="id"
                :loading="autocompleteLoading"
                @update:search="searchUsers"
              ></v-autocomplete>

              <v-alert
                v-if="testResults.autocomplete"
                :type="testResults.autocomplete.success ? 'success' : 'error'"
                density="compact"
                class="mb-3"
              >
                {{ testResults.autocomplete.message }}
              </v-alert>

              <v-btn
                color="success"
                variant="elevated"
                prepend-icon="mdi-magnify"
                @click="testAutocompletion"
                :loading="autocompleteLoading"
                block
              >
                Test Autocompletion
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Resultados de Tests -->
      <v-row>
        <v-col cols="12">
          <v-card class="elevation-6">
            <v-card-title class="bg-info text-white">
              <v-icon class="mr-2">mdi-chart-line</v-icon>
              Resultados de Tests
            </v-card-title>
            <v-card-text class="pa-4">
              <div class="d-flex justify-space-between align-center mb-4">
                <h3>Historial de Tests</h3>
                <v-btn
                  color="error"
                  variant="outlined"
                  prepend-icon="mdi-delete"
                  @click="clearTestHistory"
                  size="small"
                >
                  Limpiar Historial
                </v-btn>
              </div>

              <v-data-table
                :headers="testHeaders"
                :items="testHistory"
                density="comfortable"
                :items-per-page="10"
              >
                <template v-slot:item.timestamp="{ item }">
                  {{ formatTimestamp(item.timestamp) }}
                </template>
                
                <template v-slot:item.success="{ item }">
                  <v-chip
                    :color="item.success ? 'success' : 'error'"
                    size="small"
                    variant="elevated"
                  >
                    {{ item.success ? 'SUCCESS' : 'ERROR' }}
                  </v-chip>
                </template>

                <template v-slot:item.duration="{ item }">
                  {{ item.duration }}ms
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { FormValidationService } from '@/services/formApiService'

// Estado reactivo
const testEmail = ref('')
const testUsername = ref('')
const testFile = ref<File[]>([])
const selectedDepartment = ref('')
const selectedUser = ref('')

const emailTesting = ref(false)
const usernameTesting = ref(false)
const uploadTesting = ref(false)
const autocompleteLoading = ref(false)
const runningTests = ref(false)

const uploadProgress = ref(0)

const testResults = reactive({
  validation: null as any,
  upload: null as any,
  autocomplete: null as any
})

const departmentSuggestions = ref([
  'Tecnología',
  'Operaciones', 
  'Administración',
  'Seguridad',
  'Finanzas'
])

const userSuggestions = ref([
  { id: 1, fullName: 'Juan Pérez' },
  { id: 2, fullName: 'María García' },
  { id: 3, fullName: 'Carlos López' }
])

const testHistory = ref<any[]>([])

const testHeaders = [
  { title: 'Timestamp', key: 'timestamp' },
  { title: 'Test Type', key: 'type' },
  { title: 'Result', key: 'success' },
  { title: 'Duration', key: 'duration' },
  { title: 'Message', key: 'message' }
]

// Funciones de testing
const testEmailValidation = async () => {
  if (!testEmail.value) return
  
  emailTesting.value = true
  const startTime = Date.now()
  
  try {
    const result = await FormValidationService.validateEmailUniqueness(testEmail.value)
    const duration = Date.now() - startTime
    
    testResults.validation = {
      success: result.valid,
      message: result.valid ? 'Email disponible' : 'Email ya está en uso'
    }
    
    addToHistory('Email Validation', result.valid, duration, testResults.validation.message)
  } catch (error) {
    const duration = Date.now() - startTime
    testResults.validation = {
      success: false,
      message: 'Error en validación de email'
    }
    addToHistory('Email Validation', false, duration, 'Error en validación')
  } finally {
    emailTesting.value = false
  }
}

const testUsernameValidation = async () => {
  if (!testUsername.value) return
  
  usernameTesting.value = true
  const startTime = Date.now()
  
  try {
    const result = await FormValidationService.validateUsernameUniqueness(testUsername.value)
    const duration = Date.now() - startTime
    
    testResults.validation = {
      success: result.valid,
      message: result.valid ? 'Username disponible' : 'Username ya está en uso'
    }
    
    addToHistory('Username Validation', result.valid, duration, testResults.validation.message)
  } catch (error) {
    const duration = Date.now() - startTime
    testResults.validation = {
      success: false,
      message: 'Error en validación de username'
    }
    addToHistory('Username Validation', false, duration, 'Error en validación')
  } finally {
    usernameTesting.value = false
  }
}

const testFileUpload = () => {
  if (!testFile.value?.length) return
  simulateFileUpload()
}

const simulateFileUpload = async () => {
  uploadTesting.value = true
  uploadProgress.value = 0
  const startTime = Date.now()
  
  try {
    // Simular progreso de upload
    for (let i = 0; i <= 100; i += 10) {
      uploadProgress.value = i
      await new Promise(resolve => setTimeout(resolve, 100))
    }
    
    const duration = Date.now() - startTime
    
    testResults.upload = {
      success: true,
      message: 'File uploaded successfully'
    }
    
    addToHistory('File Upload', true, duration, 'Upload completado')
  } catch (error) {
    const duration = Date.now() - startTime
    testResults.upload = {
      success: false,
      message: 'Error en upload'
    }
    addToHistory('File Upload', false, duration, 'Error en upload')
  } finally {
    uploadTesting.value = false
    setTimeout(() => {
      uploadProgress.value = 0
    }, 2000)
  }
}

const searchDepartments = (search: string) => {
  // Simular búsqueda de departamentos
  console.log('Searching departments:', search)
}

const searchUsers = (search: string) => {
  // Simular búsqueda de usuarios
  console.log('Searching users:', search)
}

const testAutocompletion = async () => {
  autocompleteLoading.value = true
  const startTime = Date.now()
  
  try {
    // Simular test de autocompletado
    await new Promise(resolve => setTimeout(resolve, 500))
    
    const duration = Date.now() - startTime
    
    testResults.autocomplete = {
      success: true,
      message: 'Autocompletion working correctly'
    }
    
    addToHistory('Autocompletion', true, duration, 'Autocompletado funcionando')
  } catch (error) {
    const duration = Date.now() - startTime
    testResults.autocomplete = {
      success: false,
      message: 'Error en autocompletado'
    }
    addToHistory('Autocompletion', false, duration, 'Error en autocompletado')
  } finally {
    autocompleteLoading.value = false
  }
}

const runValidationTests = async () => {
  runningTests.value = true
  
  await testEmailValidation()
  await testUsernameValidation()
  
  runningTests.value = false
}

const addToHistory = (type: string, success: boolean, duration: number, message: string) => {
  testHistory.value.unshift({
    timestamp: new Date(),
    type,
    success,
    duration,
    message
  })
}

const clearTestHistory = () => {
  testHistory.value = []
}

const formatTimestamp = (timestamp: Date) => {
  return timestamp.toLocaleString()
}
</script>

<style scoped>
.api-testing-center {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}
</style>
